/**
 * Case Study PDF export — builds a presentation-quality PDF of the analyzed
 * case study from Firestore data. Used for sharing analysis with stakeholders
 * outside the REPSit app.
 *
 * Layout: cover header → summary → REPS scores → OE element analyses (5) →
 * findings → lessons → precursor patterns → tags → footer.
 */

import jsPDF from 'jspdf';
import { REPS_COLORS } from './constants';
import { isKnownIncidentDate } from './caseStudyDates.js';

/* ------------------------------------------------------------------------- */
/* Layout constants                                                           */
/* ------------------------------------------------------------------------- */

const PAGE_WIDTH = 612;    // 8.5" * 72pt
const PAGE_HEIGHT = 792;   // 11"  * 72pt
const MARGIN = 50;
const CONTENT_WIDTH = PAGE_WIDTH - MARGIN * 2;
const FOOTER_HEIGHT = 30;
const SAFE_BOTTOM = PAGE_HEIGHT - MARGIN - FOOTER_HEIGHT;

const COLORS = {
  primary: [21, 101, 192],         // blue
  dark: [33, 33, 33],
  text: [55, 71, 79],
  muted: [120, 134, 142],
  background: [248, 250, 252],
  border: [220, 224, 228],
  // REPS brand RGB — Tim's official hexes (R #6D2D9D / E #0072C3 / P #B70000 / S #00AF51)
  ...{
    reliability: [109, 45, 157],
    efficiency: [0, 114, 195],
    productivity: [183, 0, 0],
    safety: [0, 175, 81],
  },
  drift: [21, 101, 192],
  driftWork: [0, 137, 123],
  traps: [239, 108, 0],
  roadblocks: [106, 27, 154],
  landmines: [198, 40, 40],
  success: [46, 125, 50],
  severity: {
    critical: [198, 40, 40],
    major: [239, 108, 0],
    moderate: [251, 192, 45],
    minor: [21, 101, 192],
  },
};

/* ------------------------------------------------------------------------- */
/* Helper functions                                                           */
/* ------------------------------------------------------------------------- */

function newDoc() {
  return new jsPDF({ unit: 'pt', format: 'letter', orientation: 'portrait' });
}

function rgb(doc, color, op) {
  if (op === 'fill') doc.setFillColor(color[0], color[1], color[2]);
  else if (op === 'stroke') doc.setDrawColor(color[0], color[1], color[2]);
  else doc.setTextColor(color[0], color[1], color[2]);
}

function setFont(doc, opts = {}) {
  doc.setFont('helvetica', opts.style || 'normal');
  if (opts.size) doc.setFontSize(opts.size);
  if (opts.color) rgb(doc, opts.color);
}

function ensureSpace(doc, neededHeight, cursorY) {
  if (cursorY + neededHeight > SAFE_BOTTOM) {
    doc.addPage();
    return MARGIN;
  }
  return cursorY;
}

/**
 * Atomically reserve space for a block. If there isn't enough room before the
 * page footer, force a new page so the block stays together. Use this when a
 * section header + its first few lines should never be split across pages.
 */
function ensureBlock(doc, minBlockHeight, cursorY) {
  if (cursorY + minBlockHeight > SAFE_BOTTOM) {
    doc.addPage();
    return MARGIN;
  }
  return cursorY;
}

/**
 * Estimate the rendered height of an array of text strings given a max width.
 * Used to decide whether to keep a block on the current page or break early.
 */
function estimateTextBlockHeight(doc, strings, opts = {}) {
  const fontSize = opts.size || 10;
  const lineHeight = opts.lineHeight || 12;
  const padPerItem = opts.padPerItem || 4;
  const maxWidth = opts.maxWidth || CONTENT_WIDTH;
  doc.setFontSize(fontSize);
  return strings.reduce((acc, s) => {
    const lines = doc.splitTextToSize(String(s ?? ''), maxWidth).length;
    return acc + lines * lineHeight + padPerItem;
  }, 0);
}

function writeText(doc, text, x, y, opts = {}) {
  const maxWidth = opts.maxWidth || CONTENT_WIDTH;
  const lineHeight = opts.lineHeight || 14;
  setFont(doc, { size: opts.size || 10, style: opts.style, color: opts.color });
  const lines = doc.splitTextToSize(String(text ?? ''), maxWidth);
  let cursor = y;
  for (const line of lines) {
    cursor = ensureSpace(doc, lineHeight, cursor);
    doc.text(line, x, cursor);
    cursor += lineHeight;
  }
  return cursor;
}

function drawDivider(doc, y, color = COLORS.border) {
  rgb(doc, color, 'stroke');
  doc.setLineWidth(0.5);
  doc.line(MARGIN, y, PAGE_WIDTH - MARGIN, y);
}

function drawSectionHeader(doc, title, y, opts = {}) {
  // Keep header + at least 80pt of subsequent content together by default
  const minBlockHeight = opts.minNextHeight || 80;
  y = ensureBlock(doc, minBlockHeight, y);
  // Accent bar on the left
  rgb(doc, opts.accent || COLORS.primary, 'fill');
  doc.rect(MARGIN, y - 12, 4, 16, 'F');
  setFont(doc, { size: 14, style: 'bold', color: COLORS.dark });
  doc.text(title, MARGIN + 12, y);
  return y + 8;
}

function drawChip(doc, text, x, y, color, opts = {}) {
  const padX = 6;
  const height = 14;
  setFont(doc, { size: 8, style: 'bold' });
  const width = doc.getTextWidth(text) + padX * 2;
  rgb(doc, color, 'fill');
  doc.roundedRect(x, y - height + 3, width, height, 2, 2, 'F');
  if (opts.outlined) {
    rgb(doc, color, 'fill');
    doc.setFillColor(255, 255, 255);
    doc.roundedRect(x, y - height + 3, width, height, 2, 2, 'F');
    rgb(doc, color, 'stroke');
    doc.setLineWidth(0.75);
    doc.roundedRect(x, y - height + 3, width, height, 2, 2, 'S');
    rgb(doc, color);
  } else {
    doc.setTextColor(255, 255, 255);
  }
  doc.text(text, x + padX, y - 1);
  return x + width + 4;
}

function drawFooter(doc, pageNumber, totalPages, title) {
  setFont(doc, { size: 8, color: COLORS.muted });
  doc.text(
    `REPSit.ai — Drift & Accumulation Intelligent Assistant   •   ${title || 'Case Study'}`,
    MARGIN,
    PAGE_HEIGHT - 24
  );
  doc.text(`Page ${pageNumber} of ${totalPages}`, PAGE_WIDTH - MARGIN, PAGE_HEIGHT - 24, {
    align: 'right',
  });
}

function getElementColor(elementKey) {
  return COLORS[elementKey] || COLORS.primary;
}

/* ------------------------------------------------------------------------- */
/* Section renderers                                                          */
/* ------------------------------------------------------------------------- */

function renderCoverPage(doc, caseStudy) {
  // Big colored banner at the top
  rgb(doc, COLORS.primary, 'fill');
  doc.rect(0, 0, PAGE_WIDTH, 120, 'F');

  // Brand banner text (white)
  doc.setTextColor(255, 255, 255);
  setFont(doc, { size: 11, style: 'bold' });
  doc.text('REPSit', MARGIN, 50);
  setFont(doc, { size: 9 });
  doc.text('Drift & Accumulation Intelligent Assistant', MARGIN, 68);
  setFont(doc, { size: 9 });
  doc.text('Case Study Analysis', PAGE_WIDTH - MARGIN, 50, { align: 'right' });
  setFont(doc, { size: 8 });
  if (caseStudy.completedAt || caseStudy.createdAt) {
    const ts = caseStudy.completedAt || caseStudy.createdAt;
    const d = typeof ts === 'string' ? new Date(ts) : (ts?.toDate ? ts.toDate() : new Date(ts?.seconds * 1000));
    if (d && !isNaN(d)) {
      doc.text(`Generated ${d.toLocaleDateString()}`, PAGE_WIDTH - MARGIN, 68, { align: 'right' });
    }
  }

  // Main content area starts ~200pt from top to give breathing room
  let y = 220;

  // Source + industry badges
  const isCSB = caseStudy.source === 'csb';
  let chipX = MARGIN;
  if (isCSB) {
    chipX = drawChip(doc, 'CSB INVESTIGATION', chipX, y, [0, 51, 102]);
  } else {
    chipX = drawChip(doc, (caseStudy.source || 'OTHER').toUpperCase(), chipX, y, COLORS.primary);
  }
  if (caseStudy.industry) {
    const industryLabel = caseStudy.industry.replace(/_/g, ' ').toUpperCase();
    drawChip(doc, industryLabel, chipX, y, COLORS.primary, { outlined: true });
  }
  y += 30;

  // Big title
  setFont(doc, { size: 26, style: 'bold', color: COLORS.dark });
  const titleLines = doc.splitTextToSize(caseStudy.title || 'Untitled Case Study', CONTENT_WIDTH);
  for (const line of titleLines) {
    doc.text(line, MARGIN, y);
    y += 32;
  }
  y += 6;

  // Subtitle: incident details
  const parts = [];
  if (isKnownIncidentDate(caseStudy.incidentDate)) parts.push(`Incident date: ${caseStudy.incidentDate}`);
  if (caseStudy.location) parts.push(caseStudy.location);
  if (caseStudy.company) parts.push(caseStudy.company);
  if (parts.length > 0) {
    setFont(doc, { size: 11, color: COLORS.muted });
    const subtitleLines = doc.splitTextToSize(parts.join('   •   '), CONTENT_WIDTH);
    for (const line of subtitleLines) {
      doc.text(line, MARGIN, y);
      y += 18;
    }
  }

  // REPS at-a-glance preview (mini bars on cover)
  if (caseStudy.repsMapping) {
    y += 30;
    setFont(doc, { size: 9, style: 'bold', color: COLORS.muted });
    doc.text('REPS IMPACT AT A GLANCE', MARGIN, y);
    y += 16;
    const quadrants = [
      { key: 'reliability', label: 'R' },
      { key: 'efficiency', label: 'E' },
      { key: 'productivity', label: 'P' },
      { key: 'safety', label: 'S' },
    ];
    const cellWidth = (CONTENT_WIDTH - 30) / 4;
    let cx = MARGIN;
    for (const q of quadrants) {
      const score = caseStudy.repsMapping[q.key];
      if (score == null) continue;
      // Card
      rgb(doc, COLORS.background, 'fill');
      doc.roundedRect(cx, y, cellWidth, 60, 4, 4, 'F');
      // Label
      setFont(doc, { size: 9, color: COLORS.muted, style: 'bold' });
      doc.text(q.label, cx + 10, y + 16);
      // Score
      setFont(doc, { size: 22, style: 'bold', color: COLORS[q.key] });
      doc.text(`${score}`, cx + 10, y + 44);
      const scoreWidth = doc.getTextWidth(`${score}`);
      setFont(doc, { size: 9, color: COLORS.muted });
      // P5-16: place the "/10" right after the number (a fixed offset gave "10/ 10")
      doc.text('/10', cx + 10 + scoreWidth + 2, y + 44);
      cx += cellWidth + 10;
    }
    y += 80;
  }

  // Footer credit at bottom of cover page
  setFont(doc, { size: 8, color: COLORS.muted });
  doc.text(
    'Auto-generated by REPSit.ai from the source investigation report, using Principle-Based Performance Improvement methodology.',
    PAGE_WIDTH / 2,
    PAGE_HEIGHT - 60,
    { align: 'center', maxWidth: CONTENT_WIDTH }
  );

  // Always end cover with new page so content starts fresh
  doc.addPage();
  return MARGIN + 12;
}

function renderSummary(doc, caseStudy, y) {
  if (!caseStudy.summary) return y;
  y = drawSectionHeader(doc, 'Executive Summary', y);
  y = writeText(doc, caseStudy.summary, MARGIN, y + 14, { size: 10, lineHeight: 14, color: COLORS.text });
  return y + 10;
}

function renderRepsMapping(doc, caseStudy, y) {
  const mapping = caseStudy.repsMapping;
  if (!mapping) return y;
  y = drawSectionHeader(doc, 'REPS Impact Assessment', y);
  y += 14;
  setFont(doc, { size: 9, color: COLORS.muted });
  doc.text('Scores indicate impact on each REPS quadrant (0 = no impact, 10 = critical).', MARGIN, y);
  y += 16;

  const quadrants = [
    { key: 'reliability', label: 'Reliability' },
    { key: 'efficiency', label: 'Efficiency' },
    { key: 'productivity', label: 'Productivity' },
    { key: 'safety', label: 'Safety' },
  ];

  for (const q of quadrants) {
    const score = mapping[q.key];
    if (score == null) continue;
    y = ensureSpace(doc, 30, y);
    setFont(doc, { size: 10, style: 'bold', color: COLORS.dark });
    doc.text(q.label, MARGIN, y);
    setFont(doc, { size: 10, style: 'bold', color: COLORS[q.key] || COLORS.primary });
    doc.text(`${score}/10`, PAGE_WIDTH - MARGIN, y, { align: 'right' });
    y += 6;
    // Background bar
    rgb(doc, COLORS.border, 'fill');
    doc.roundedRect(MARGIN, y, CONTENT_WIDTH, 8, 4, 4, 'F');
    // Fill bar
    rgb(doc, COLORS[q.key] || COLORS.primary, 'fill');
    doc.roundedRect(MARGIN, y, CONTENT_WIDTH * (score / 10), 8, 4, 4, 'F');
    y += 20;
  }
  return y + 8;
}

function renderOEElements(doc, caseStudy, y) {
  const oe = caseStudy.oeElements;
  if (!oe || Object.keys(oe).length === 0) return y;
  y = drawSectionHeader(doc, 'Organizational Entropy Analysis', y);
  y += 14;
  setFont(doc, { size: 9, color: COLORS.muted });
  doc.text(
    'Each OE element analyzed independently against the source document by Claude Opus.',
    MARGIN,
    y
  );
  y += 18;

  const elements = [
    { key: 'driftPerception', label: 'Drift in Perception of Risk' },
    { key: 'driftWork', label: 'Drift in How Work is Done' },
    { key: 'traps', label: 'Accumulation: Traps' },
    { key: 'roadblocks', label: 'Accumulation: Roadblocks' },
    { key: 'landmines', label: 'Accumulation: Landmines' },
  ];

  for (const el of elements) {
    const data = oe[el.key];
    if (!data) continue;

    // Pre-estimate this block's height so we don't orphan the header + first
    // few findings at the bottom of a page. Header ~30pt + findings + evidence.
    const findings = Array.isArray(data.findings) ? data.findings : [];
    const evidence = Array.isArray(data.evidence) ? data.evidence : [];
    const findingsHeight = estimateTextBlockHeight(doc, findings.slice(0, 3), {
      size: 9, lineHeight: 12, padPerItem: 4, maxWidth: CONTENT_WIDTH - 22,
    });
    const evidenceHeight = estimateTextBlockHeight(doc, evidence.slice(0, 2), {
      size: 8, lineHeight: 11, padPerItem: 4, maxWidth: CONTENT_WIDTH - 22,
    });
    // Header (30) + Findings label (10) + first 3 findings + Evidence label (10) + first 2 quotes
    const minBlockHeight = 50 + findingsHeight + evidenceHeight;
    y = ensureBlock(doc, minBlockHeight, y);

    // Accent bar + heading + score chip
    rgb(doc, getElementColor(el.key), 'fill');
    doc.rect(MARGIN, y - 12, 4, 16, 'F');
    setFont(doc, { size: 12, style: 'bold', color: COLORS.dark });
    doc.text(el.label, MARGIN + 12, y);

    let chipX = PAGE_WIDTH - MARGIN;
    if (typeof data.confidence === 'number') {
      const text = `Conf ${Math.round(data.confidence * 100)}%`;
      setFont(doc, { size: 8, style: 'bold' });
      const w = doc.getTextWidth(text) + 12;
      chipX -= w;
      rgb(doc, COLORS.muted, 'fill');
      doc.setFillColor(255, 255, 255);
      doc.roundedRect(chipX, y - 9, w, 14, 2, 2, 'F');
      rgb(doc, COLORS.muted, 'stroke');
      doc.setLineWidth(0.5);
      doc.roundedRect(chipX, y - 9, w, 14, 2, 2, 'S');
      rgb(doc, COLORS.muted);
      doc.text(text, chipX + 6, y);
      chipX -= 4;
    }
    if (typeof data.score === 'number') {
      const text = `Score ${data.score}/10`;
      setFont(doc, { size: 8, style: 'bold' });
      const w = doc.getTextWidth(text) + 12;
      chipX -= w;
      rgb(doc, getElementColor(el.key), 'fill');
      doc.roundedRect(chipX, y - 9, w, 14, 2, 2, 'F');
      doc.setTextColor(255, 255, 255);
      doc.text(text, chipX + 6, y);
    }
    y += 10;

    // Findings — note: `findings` already declared above for pre-estimate
    if (findings.length > 0) {
      y += 6;
      setFont(doc, { size: 8, style: 'bold', color: COLORS.muted });
      doc.text('FINDINGS', MARGIN + 12, y);
      y += 10;
      for (const f of findings) {
        y = ensureSpace(doc, 16, y);
        rgb(doc, getElementColor(el.key), 'fill');
        doc.circle(MARGIN + 15, y - 3, 1.5, 'F');
        y = writeText(doc, f, MARGIN + 22, y, {
          maxWidth: CONTENT_WIDTH - 22,
          size: 9,
          lineHeight: 12,
          color: COLORS.text,
        });
        y += 4;
      }
    }

    // Evidence — note: `evidence` already declared above for pre-estimate
    if (evidence.length > 0) {
      y += 6;
      setFont(doc, { size: 8, style: 'bold', color: COLORS.muted });
      doc.text('EVIDENCE FROM SOURCE', MARGIN + 12, y);
      y += 10;
      for (const ev of evidence) {
        y = ensureSpace(doc, 14, y);
        setFont(doc, { size: 8, color: COLORS.muted });
        doc.text('"', MARGIN + 12, y);
        y = writeText(doc, ev, MARGIN + 22, y, {
          maxWidth: CONTENT_WIDTH - 22,
          size: 8,
          lineHeight: 11,
          color: COLORS.muted,
          style: 'italic',
        });
        y += 4;
      }
    }
    y += 14;
  }
  return y;
}

function renderKeyFindings(doc, caseStudy, y) {
  const findings = caseStudy.keyFindings;
  if (!findings || findings.length === 0) return y;
  y = drawSectionHeader(doc, 'Key Findings', y);
  y += 14;
  for (const f of findings) {
    if (typeof f !== 'object') continue;
    // Pre-estimate height — meta row (14) + finding text + padding
    const findingHeight = estimateTextBlockHeight(doc, [f.finding || ''], {
      size: 9, lineHeight: 12, padPerItem: 0, maxWidth: CONTENT_WIDTH - 10,
    });
    y = ensureBlock(doc, 14 + findingHeight + 14, y);
    // Severity bar
    const sevColor = COLORS.severity[f.severity] || COLORS.muted;
    rgb(doc, sevColor, 'fill');
    doc.rect(MARGIN, y - 10, 3, 14, 'F');
    setFont(doc, { size: 8, style: 'bold', color: sevColor });
    doc.text((f.severity || '').toUpperCase(), MARGIN + 10, y);
    if (f.oeElement) {
      setFont(doc, { size: 8, color: COLORS.muted });
      doc.text(`OE: ${f.oeElement}`, MARGIN + 80, y);
    }
    if (f.repsQuadrant) {
      setFont(doc, { size: 8, color: COLORS[f.repsQuadrant] || COLORS.primary, style: 'bold' });
      doc.text(`REPS: ${f.repsQuadrant.toUpperCase()}`, MARGIN + 180, y);
    }
    y += 4;
    y = writeText(doc, f.finding || '', MARGIN + 10, y + 10, {
      maxWidth: CONTENT_WIDTH - 10,
      size: 9,
      lineHeight: 12,
      color: COLORS.text,
    });
    y += 10;
  }
  return y;
}

function renderLessonsLearned(doc, caseStudy, y) {
  const lessons = caseStudy.lessonsLearned;
  if (!lessons || lessons.length === 0) return y;
  y = drawSectionHeader(doc, 'Lessons Learned', y);
  y += 14;
  for (const l of lessons) {
    if (typeof l !== 'object') continue;
    // Pre-estimate height for the whole lesson card
    const lessonHeight = estimateTextBlockHeight(doc, [l.lesson || ''], {
      size: 9, lineHeight: 12, padPerItem: 0, maxWidth: CONTENT_WIDTH - 10,
    });
    const applicabilityHeight = l.applicability
      ? estimateTextBlockHeight(doc, [`Applies to: ${l.applicability}`], {
          size: 8, lineHeight: 10, padPerItem: 0, maxWidth: CONTENT_WIDTH - 10,
        })
      : 0;
    y = ensureBlock(doc, 14 + lessonHeight + applicabilityHeight + 18, y);
    const pri = (l.priority || '').toLowerCase();
    const priColor =
      pri === 'high' ? COLORS.severity.critical
        : pri === 'medium' ? COLORS.severity.major
          : COLORS.severity.minor;
    rgb(doc, priColor, 'fill');
    doc.rect(MARGIN, y - 10, 3, 14, 'F');
    setFont(doc, { size: 8, style: 'bold', color: priColor });
    doc.text(`${(l.priority || 'medium').toUpperCase()} PRIORITY`, MARGIN + 10, y);
    y += 4;
    y = writeText(doc, l.lesson || '', MARGIN + 10, y + 10, {
      maxWidth: CONTENT_WIDTH - 10,
      size: 9,
      lineHeight: 12,
      color: COLORS.text,
    });
    if (l.applicability) {
      y += 2;
      y = writeText(doc, `Applies to: ${l.applicability}`, MARGIN + 10, y + 8, {
        maxWidth: CONTENT_WIDTH - 10,
        size: 8,
        lineHeight: 10,
        color: COLORS.muted,
        style: 'italic',
      });
    }
    y += 10;
  }
  return y;
}

function renderPrecursorPatterns(doc, caseStudy, y) {
  const patterns = caseStudy.precursorPatterns;
  if (!patterns || patterns.length === 0) return y;
  y = drawSectionHeader(doc, 'Precursor Patterns', y, { accent: COLORS.severity.major });
  y += 14;
  setFont(doc, { size: 9, color: COLORS.muted });
  doc.text(
    'Use these patterns to detect similar drift signatures in other organizations.',
    MARGIN,
    y
  );
  y += 16;

  for (const p of patterns) {
    if (typeof p !== 'object') continue;
    const descHeight = estimateTextBlockHeight(doc, [p.description || ''], {
      size: 9, lineHeight: 12, padPerItem: 0, maxWidth: CONTENT_WIDTH,
    });
    y = ensureBlock(doc, 18 + descHeight + 12, y);
    setFont(doc, { size: 10, style: 'bold', color: COLORS.dark });
    doc.text(p.patternType || 'pattern', MARGIN, y);
    if (p.matchCriteria) {
      const mc = p.matchCriteria;
      const criteriaText = `${mc.element} ${mc.operator} ${mc.value}`;
      setFont(doc, { size: 8, color: COLORS.muted });
      doc.text(`Match: ${criteriaText}`, PAGE_WIDTH - MARGIN, y, { align: 'right' });
    }
    y += 4;
    y = writeText(doc, p.description || '', MARGIN, y + 10, {
      size: 9,
      lineHeight: 12,
      color: COLORS.text,
    });
    y += 12;
  }
  return y;
}

function renderTags(doc, caseStudy, y) {
  const tags = caseStudy.tags;
  if (!tags || tags.length === 0) return y;
  y = drawSectionHeader(doc, 'Tags', y);
  y += 12;
  let x = MARGIN;
  const chipHeight = 16;
  for (const t of tags) {
    setFont(doc, { size: 8 });
    const w = doc.getTextWidth(t) + 14;
    if (x + w > PAGE_WIDTH - MARGIN) {
      x = MARGIN;
      y += chipHeight + 4;
      y = ensureSpace(doc, chipHeight + 4, y);
    }
    rgb(doc, COLORS.border, 'stroke');
    doc.setLineWidth(0.5);
    doc.roundedRect(x, y - chipHeight + 3, w, chipHeight, 3, 3, 'S');
    rgb(doc, COLORS.text);
    doc.text(t, x + 7, y - 4);
    x += w + 4;
  }
  return y + chipHeight + 8;
}

/* ------------------------------------------------------------------------- */
/* Public API                                                                 */
/* ------------------------------------------------------------------------- */

/**
 * Internal — build the jsPDF document and return it along with the suggested
 * download filename. Shared by the download and preview paths so they produce
 * identical PDFs.
 */
function buildCaseStudyPDFDoc(caseStudy) {
  if (!caseStudy) throw new Error('caseStudy required');

  const doc = newDoc();

  // Page 1: dedicated cover — title, source badges, REPS at-a-glance preview.
  // Content starts on page 2 to give the cover proper presentation weight.
  let y = renderCoverPage(doc, caseStudy);

  y = renderSummary(doc, caseStudy, y);
  y = renderOEElements(doc, caseStudy, y + 12);
  y = renderRepsMapping(doc, caseStudy, y + 12);
  y = renderKeyFindings(doc, caseStudy, y + 12);
  y = renderLessonsLearned(doc, caseStudy, y + 12);
  y = renderPrecursorPatterns(doc, caseStudy, y + 12);
  y = renderTags(doc, caseStudy, y + 12);

  // Page footers
  const totalPages = doc.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    drawFooter(doc, i, totalPages, caseStudy.title);
  }

  const safeTitle = (caseStudy.title || 'case-study')
    .replace(/[^a-zA-Z0-9\- ]/g, '')
    .replace(/\s+/g, '_')
    .slice(0, 60);
  const filename = `REPSit_${safeTitle}.pdf`;

  return { doc, filename };
}

/**
 * Generate a PDF for a case study and trigger download.
 *
 * @param {Object} caseStudy - Full case study document data from Firestore
 * @returns {void} (initiates browser download)
 */
export function exportCaseStudyPDF(caseStudy) {
  const { doc, filename } = buildCaseStudyPDFDoc(caseStudy);
  doc.save(filename);
}

/**
 * Generate a PDF for a case study and return a Blob URL suitable for previewing
 * in an <iframe>. Caller is responsible for calling URL.revokeObjectURL(url)
 * when the preview is dismissed to free memory.
 *
 * @param {Object} caseStudy - Full case study document data from Firestore
 * @returns {{ url: string, blob: Blob, filename: string }}
 */
export function generateCaseStudyPDFBlobUrl(caseStudy) {
  const { doc, filename } = buildCaseStudyPDFDoc(caseStudy);
  const blob = doc.output('blob');
  const url = URL.createObjectURL(blob);
  return { url, blob, filename };
}
